/**
 * Sprint 23.6 v2 — AgentScorecard (Hero section 6: this-week rollup).
 *
 * Four metric cards (Q9) in a 4-column grid. Each shows the current count and a
 * delta vs the prior rolling 7 days. Per the visual contract the delta colour
 * rule is simple: a positive delta is success-green, everything else is
 * tertiary (a negative AI-spend delta is good, not bad — so never danger-red).
 *
 * Driven by `get_agent_productivity` via `useAgentProductivity` (60s cache).
 *
 * Empty state (Q25): zeros with "—" deltas on a fresh hire — the supervisor
 * sees a brand-new agent, not a broken page.
 */

import { type CSSProperties } from "react";
import { useAgentProductivity } from "../../hooks/use-agent-productivity";
import { useThemedLabels } from "../../hooks/use-themed-labels";
import { MetricScorecard } from "../scorecard/MetricScorecard";
import type { MetricCardProps } from "../scorecard/MetricCard";

export interface AgentScorecardProps {
  agentId: string;
}

interface MetricSpec {
  key: "tasks_completed" | "retros_composed" | "learnings_captured" | "ai_spend_usd";
  label: string;
  formatValue: (n: number) => string;
  formatDelta: (n: number) => string;
}

const METRICS: MetricSpec[] = [
  { key: "tasks_completed", label: "Tasks done", formatValue: (n) => String(n), formatDelta: formatSigned },
  { key: "retros_composed", label: "Retros", formatValue: (n) => String(n), formatDelta: formatSigned },
  { key: "learnings_captured", label: "Learnings", formatValue: (n) => String(n), formatDelta: formatSigned },
  {
    key: "ai_spend_usd",
    label: "AI spend",
    formatValue: (n) => `$${n.toFixed(2)}`,
    formatDelta: (n) => `${n >= 0 ? "+" : "-"}$${Math.abs(n).toFixed(2)}`,
  },
];

function formatSigned(n: number): string {
  return n > 0 ? `+${n}` : `${n}`;
}

export function AgentScorecard({ agentId }: AgentScorecardProps) {
  const labels = useThemedLabels();
  const query = useAgentProductivity(agentId);
  const data = query.data;

  const metrics: MetricCardProps[] = METRICS.map((m) => {
    const value = data ? Number(data.current[m.key] ?? 0) : 0;
    const delta = data ? Number(data.deltas[m.key] ?? 0) : 0;
    const hasPrior = data ? Number(data.prior[m.key] ?? 0) > 0 || delta !== 0 : false;
    return {
      label: m.label,
      value: query.isLoading ? "…" : m.formatValue(value),
      delta: !hasPrior ? "—" : delta === 0 ? "=" : m.formatDelta(delta),
      deltaTone: hasPrior && delta > 0 ? "success" : "tertiary",
      deltaTitle: hasPrior ? "vs prior 7 days" : "no prior period",
    };
  });

  return <MetricScorecard title={labels.productivityTitle} metrics={metrics} style={wrap} />;
}

const wrap: CSSProperties = {
  padding: "12px 0 14px",
  borderBottom: "0.5px solid var(--color-border-tertiary)",
};
